import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { UiMoleculeInputComponent } from '../../molecules/ui-molecule-input/ui-molecule-input.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const SEARCH_MAX = 128;
const SEARCH_TRIGGER_MIN = 3;

@Component({
  selector: 'ui-atom-global-search',
  standalone: true,
  imports: [UiMoleculeInputComponent, ReactiveFormsModule, TranslatePipe],
  template: `
    <div class="global-search-wrapper">
      <div class="global-search-container">
        <ui-molecule-input
          textId="globalSearch"
          [placeholder]="placeholder || ('tables.filters.globalSearch' | translate)"
          [formControl]="searchControl"
          [maxLength]="searchMax"
          [errorMessage]="searchError"
          [hasSuffix]="true"
        >
          @if (isSearching) {
            <span uiInputSuffix class="search-loader" aria-hidden="true">
              <span class="spinner"></span>
            </span>
          } @else if (searchControl.value && searchControl.value.trim() !== '') {
            <button
              uiInputSuffix
              class="clear-search-btn"
              (click)="clearSearch()"
              [attr.aria-label]="'common.search.clear' | translate"
              [title]="'common.search.clear' | translate"
              type="button"
            >
              ×
            </button>
          }
        </ui-molecule-input>
      </div>
    </div>
  `,
  styles: [
    `
      .global-search-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 1rem 0 1.5rem 0;
      }

      .global-search-container {
        width: 100%;
        max-width: 400px;
      }

      .search-loader {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        border: 2px solid #e2e8f0;
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      .clear-search-btn {
        background: none;
        border: none;
        color: #94a3b8;
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 0.25rem;
        font-size: 1rem;
        line-height: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background-color: #f1f5f9;
          color: #64748b;
        }
      }
    `,
  ],
})
export class UiAtomGlobalSearchComponent {
  @Input() placeholder?: string;
  @Output() search = new EventEmitter<string>();

  readonly searchMax = SEARCH_MAX;
  readonly searchMin = SEARCH_TRIGGER_MIN;
  protected searchControl = new FormControl('', {
    validators: [
      Validators.maxLength(SEARCH_MAX),
      Validators.minLength(SEARCH_TRIGGER_MIN),
    ],
  });
  protected isSearching = false;
  private searchTimeout: any;

  constructor(private translate: TranslateService) {
    this.searchControl.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      if (this.searchControl.invalid) {
        // Mientras el control sea inválido (< 3 o > 128 chars), no disparamos búsqueda.
        this.isSearching = false;
        return;
      }

      const trimmed = (value || '').trim();

      if (trimmed.length >= SEARCH_TRIGGER_MIN) {
        this.isSearching = true;
        this.searchTimeout = setTimeout(() => {
          this.search.emit(value || '');
          this.isSearching = false;
        }, 500);
      } else if (trimmed === '') {
        // Limpieza total → resetear filtro en el padre
        this.isSearching = false;
        this.search.emit('');
      } else {
        // 1-2 caracteres: no emitir, sólo limpiar estado visual
        this.isSearching = false;
      }
    });
  }

  get searchError(): string | null {
    const c = this.searchControl;
    if (!c.touched && !c.dirty) return null;
    if (c.hasError('maxlength')) {
      return this.translate.instant('common.search.error.maxLength', { max: this.searchMax });
    }
    if (c.hasError('minlength')) {
      return this.translate.instant('common.search.error.minLength', { min: this.searchMin });
    }
    return null;
  }

  clearSearch(): void {
    this.isSearching = false;
    this.searchControl.setValue('');
  }
}
